import React, { useState, useRef, useCallback } from 'react';
import JSZip from 'jszip';
import { optimizeImage, formatSize, ImageItem, ImageSettings } from './converters';

const PRESETS: Record<string, Partial<ImageSettings> & { qualityPct: number }> = {
  coupang780: { maxW: 780, maxH: 0, format: 'jpeg', qualityPct: 85, maxKB: 1500 },
  coupang860: { maxW: 860, maxH: 0, format: 'jpeg', qualityPct: 80, maxKB: 1500 },
  coupang1200: { maxW: 1200, maxH: 0, format: 'jpeg', qualityPct: 80, maxKB: 1500 },
};

function ImageOptimizerTab() {
  const [activePreset, setActivePreset] = useState('coupang780');
  const [width, setWidth] = useState(780);
  const [height, setHeight] = useState(0);
  const [format, setFormat] = useState('jpeg');
  const [qualityPct, setQualityPct] = useState(85);
  const [maxKB, setMaxKB] = useState(1500);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [toast, setToast] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  const getSettings = useCallback((): ImageSettings => ({
    maxW: width,
    maxH: height,
    format,
    quality: qualityPct / 100,
    maxKB,
  }), [width, height, format, qualityPct, maxKB]);

  const applyPreset = (name: string) => {
    setActivePreset(name);
    const p = PRESETS[name];
    if (!p) return;
    setWidth(p.maxW!);
    setHeight(p.maxH!);
    setFormat(p.format!);
    setQualityPct(p.qualityPct);
    setMaxKB(p.maxKB!);
  };

  const handleFiles = (files: FileList) => {
    const settings = getSettings();
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const item: ImageItem = {
            id: Date.now() + Math.random(),
            name: file.name,
            origWidth: img.naturalWidth,
            origHeight: img.naturalHeight,
            origSize: file.size,
            origDataURL: e.target!.result as string,
            img,
            result: null,
          };
          item.result = optimizeImage(item, settings);
          setImages(prev => [...prev, item]);
        };
        img.src = e.target!.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const reprocessAll = () => {
    const settings = getSettings();
    setImages(prev => prev.map(item => ({
      ...item,
      result: optimizeImage(item, settings),
    })));
    showToast('전체 이미지가 다시 최적화되었습니다!');
  };

  const removeImage = (idx: number) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  const downloadImage = (idx: number) => {
    const item = images[idx];
    if (!item?.result) return;
    const a = document.createElement('a');
    a.href = item.result.dataURL;
    a.download = item.result.fileName;
    a.click();
  };

  const downloadAll = async () => {
    const items = images.filter(i => i.result);
    if (!items.length) return;
    showToast('ZIP 파일 생성 중...');
    const zip = new JSZip();
    items.forEach((item, idx) => {
      const r = item.result!;
      const base64 = r.dataURL.split(',')[1];
      const num = String(idx + 1).padStart(2, '0');
      zip.file(`${num}_${r.fileName}`, base64, { base64: true });
    });
    const blob = await zip.generateAsync({ type: 'blob' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'coupang_images_optimized.zip';
    a.click();
    URL.revokeObjectURL(a.href);
    showToast(`${items.length}장이 ZIP 파일로 다운로드되었습니다!`);
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Settings Sidebar */}
      <div className="w-80 bg-gray-50 border-r border-gray-200 p-5 flex flex-col gap-4 overflow-y-auto flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-800">이미지 최적화 설정</h3>

        {/* 쿠팡 조건 */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">쿠팡 이미지 업로드 조건</div>
          <div className="text-xs text-jeju-brown leading-relaxed">
            가로: <strong>300px 이상 ~ 3,000px 이하</strong><br />
            세로: <strong>30,000px 이하</strong>
          </div>
        </div>

        {/* 프리셋 */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-3">프리셋</div>
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'coupang780', label: '780px' },
              { key: 'coupang860', label: '860px' },
              { key: 'coupang1200', label: '1200px' },
            ].map(p => (
              <button
                key={p.key}
                onClick={() => applyPreset(p.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  activePreset === p.key
                    ? 'border-jeju-brown text-jeju-brown bg-jeju-brown/5'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
              >{p.label}</button>
            ))}
          </div>
        </div>

        {/* 출력 크기 */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-3">출력 크기</div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1 font-medium">가로 (px)</label>
              <input type="number" value={width} onChange={e => { setWidth(+e.target.value); setActivePreset('custom'); }} min={300} max={3000}
                className="w-full bg-gray-50 border border-gray-200 rounded-md text-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown focus:border-jeju-brown" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1 font-medium">최대 세로 (px)</label>
              <input type="number" value={height} onChange={e => { setHeight(+e.target.value); setActivePreset('custom'); }} min={0} max={30000} placeholder="0=자동"
                className="w-full bg-gray-50 border border-gray-200 rounded-md text-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown focus:border-jeju-brown" />
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">세로 0 = 비율 유지 자동 조절</p>
        </div>

        {/* 품질 / 포맷 */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-3">품질 / 포맷</div>
          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1 font-medium">포맷</label>
            <select value={format} onChange={e => { setFormat(e.target.value); setActivePreset('custom'); }}
              className="w-full bg-gray-50 border border-gray-200 rounded-md text-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown focus:border-jeju-brown">
              <option value="jpeg">JPEG (사진 추천)</option>
              <option value="png">PNG (투명 배경)</option>
              <option value="webp">WebP (최고 압축)</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1 font-medium">품질</label>
            <div className="flex items-center gap-3">
              <input type="range" min={10} max={100} value={qualityPct} onChange={e => { setQualityPct(+e.target.value); setActivePreset('custom'); }}
                className="flex-1 accent-jeju-brown" />
              <span className="text-sm text-gray-800 font-semibold min-w-[36px] text-right">{qualityPct}%</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1 font-medium">최대 파일 크기 (KB)</label>
            <input type="number" value={maxKB} onChange={e => { setMaxKB(+e.target.value); setActivePreset('custom'); }} placeholder="0=제한없음"
              className="w-full bg-gray-50 border border-gray-200 rounded-md text-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-jeju-brown focus:border-jeju-brown" />
            <p className="text-[11px] text-gray-400 mt-1">쿠팡 권장: 500KB 이하. 0=제한 없음</p>
          </div>
        </div>

        <button
          onClick={reprocessAll}
          className="w-full py-2.5 bg-jeju-brown text-white rounded-lg text-sm font-medium hover:bg-jeju-brown-dark transition-colors"
        >전체 다시 최적화</button>
      </div>

      {/* Main Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Drop Zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileInputRef.current?.click()}
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl m-5 cursor-pointer transition-all relative ${
            dragOver ? 'border-jeju-brown bg-jeju-brown/5' : 'border-gray-300 hover:border-gray-400'
          }`}
          style={{ flex: images.length ? '0 0 120px' : 1 }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => e.target.files && handleFiles(e.target.files)}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
          <div className={`${images.length ? 'text-3xl mb-1' : 'text-5xl mb-3'} opacity-30`}>🖼</div>
          <p className={`${images.length ? 'text-xs' : 'text-sm'} text-gray-400 text-center leading-relaxed`}>
            이미지를 여기에 드래그하거나 클릭하여 선택하세요
          </p>
        </div>

        {/* Results */}
        {images.length > 0 && (
          <div className="flex-1 overflow-y-auto p-4 flex flex-wrap gap-3 content-start">
            {images.map((item, idx) => {
              if (!item.result) return null;
              const r = item.result;
              const reduction = Math.round((1 - r.size / item.origSize) * 100);
              return (
                <div key={item.id} className={`bg-white border rounded-lg overflow-hidden w-[220px] flex-shrink-0 shadow-sm ${r.isValid ? 'border-gray-200' : 'border-red-300'}`}>
                  <img src={r.dataURL} alt={item.name} className="w-full h-[150px] object-cover bg-gray-100 block" />
                  <div className="p-3">
                    <div className="text-xs font-semibold text-gray-800 truncate mb-1.5" title={item.name}>{item.name}</div>
                    <div className="mb-1">
                      {r.isValid
                        ? <span className="text-green-600 font-bold text-[11px]">&#10004; 쿠팡 업로드 가능</span>
                        : <span className="text-red-500 font-bold text-[11px]">&#10008; {r.errors.join(', ')}</span>
                      }
                    </div>
                    <div className="text-[11px] text-gray-400 leading-relaxed">
                      원본: {item.origWidth}x{item.origHeight} / {formatSize(item.origSize)}<br />
                      변환: {r.width}x{r.height} / {formatSize(r.size)}<br />
                      <span className="text-green-600 font-semibold">▼ {reduction}% 감소</span> · 품질 {r.quality}% · {r.format.toUpperCase()}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => downloadImage(idx)} className="px-2.5 py-1 text-[10px] bg-jeju-brown text-white rounded font-medium hover:bg-jeju-brown-dark transition-colors">다운로드</button>
                      <button onClick={() => removeImage(idx)} className="px-2.5 py-1 text-[10px] border border-gray-200 text-gray-500 rounded font-medium hover:bg-gray-50 transition-colors">삭제</button>
                    </div>
                  </div>
                </div>
              );
            })}
            {images.length > 1 && (
              <div className="w-full flex justify-center py-3">
                <button onClick={downloadAll} className="px-5 py-2 text-sm bg-jeju-brown text-white rounded-lg font-medium hover:bg-jeju-brown-dark transition-colors">
                  전체 다운로드 ({images.length}장)
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-jeju-brown text-white px-6 py-2.5 rounded-lg text-sm font-semibold z-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

export default ImageOptimizerTab;
